import React, { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import {
  CloseOutlined,
  CloseCircleFilled,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import MobilePageLayout from '../../../components/layout/MobilePageLayout';
import DSLoading from '../../../components/designSystem/DSLoading';
import { convertToVwMob } from '../../../Utils/responsive';

import hugeIcon from '../../../assets/Icon/project/huge_icon.png';
import linkIcon from '../../../assets/Icon/project/link_icon.png';
import weuiArrowIcon from '../../../assets/Icon/project/weui_arrow-outlined.png';
import logoImg from '../../../assets/Logo/main_logo.png';
import loadingLottie from '../../../assets/Icon/lottie/Insider-loading.lottie';
import { PATHS } from '../../../routes/paths';
import DSBottomSheet from '../../../components/designSystem/DSBottomSheet';
import DSModal from '../../../components/designSystem/DSModal';
import ProfileIncompleteModal from '../../../components/common/ProfileIncompleteModal';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { createWithdraw, getWithdraw, searchWithdrawProjects, updateProject, updateWithdraw } from '../../../Module/Project/Presentation/projectActions';
import { selectProjects, selectProjectsStatus } from '../../../Module/Project/Presentation/projectSelectors';
import { selectSession } from '../../../Module/Auth/Presentation/authSelectors';
import { selectUserProfile } from '../../../Module/User/Presentation/userSelectors';
import { updateUser } from '../../../Module/User/Presentation/userActions';
import { theme } from '../../../styles/theme';
import { useTranslation } from 'react-i18next';
import { getYouTubeThumbnail } from '../../../Utils/youtube';
import { getCloudFrontImageUrl, THUMBNAIL_DIMENSIONS } from '../../../Utils/cloudFrontImage';
import { canCreateNewProject } from '../../../Utils/projectCreationGuard';

type FundingType = 'recurring' | 'oneTime';
type ProjectStatus = 'inProgress' | 'done';
type PayAction = 'request' | 'completed';

type ProjectCardItem = {
  id: string;
  withdrawProjectId: string;
  projectStatus: string;
  title: string;
  dateText: string;
  fundingTypeLabel: FundingType;
  statusLabel: ProjectStatus;
  imageUrl?: string;
  payAction?: PayAction;
};

type PayFlowState =
  | { step: 'idle' }
  | { step: 'processing' }
  | { step: 'inProgress' }
  | {
      step: 'codeReady';
      code: string;
      input: string;
      failCount: number;
      error?: string;
      withdrawId?: string;
      requestCode?: string;
      withdrawSnapshot?: Record<string, unknown>;
    }
  | { step: 'done' }
  | { step: 'stopped'; failCount: number };

const toBooleanFlag = (value: unknown) => {
  if (typeof value === 'boolean') return value;
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'y';
};

const toTrialCount = (value: unknown, fallback = 0) => {
  const parsed = Number(value ?? fallback);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return parsed;
};

const resolveWithdrawPayFlow = ({
  snapshot,
  withdrawId,
  code,
  requestCode,
  fallbackFailCount = 0,
}: {
  snapshot?: Record<string, unknown>;
  withdrawId?: string;
  code?: string;
  requestCode?: string;
  fallbackFailCount?: number;
}): PayFlowState => {
  const data = snapshot ?? {};
  const failCount = toTrialCount((data as any)?.user_trials ?? (data as any)?.trials, fallbackFailCount);
  const resolvedWithdrawId = String(withdrawId ?? (data as any)?.withdraw_id ?? '').trim();
  const isSentAuthenticationCode = toBooleanFlag((data as any)?.is_sent_authentication_code);
  const isResponseAuthenticationCode = toBooleanFlag((data as any)?.is_response_authentication_code);
  const isSentFund = toBooleanFlag((data as any)?.is_sent_fund);

  if (isSentFund) {
    return { step: 'done' };
  }

  if (isSentAuthenticationCode && isResponseAuthenticationCode) {
    return { step: 'inProgress' };
  }

  if (isSentAuthenticationCode && !isResponseAuthenticationCode) {
    if (failCount >= 3) {
      return { step: 'stopped', failCount };
    }
    return {
      step: 'codeReady',
      code: code ?? '',
      requestCode: requestCode ?? '',
      withdrawId,
      withdrawSnapshot: data,
      input: '',
      failCount,
    };
  }

  // withdraw가 생성된 상태(요청 완료)인데 아직 인증코드가 발송되지 않은 구간은 처리중으로 본다.
  if (resolvedWithdrawId) {
    return { step: 'processing' };
  }

  // 지급요청이 생성되지 않았거나(code 발송 전) 기본 상태는 idle로 유지한다.
  return { step: 'idle' };
};

const sanitizeProjectTitle = (value: string) => value.replace(/<br\s*\/?>/gi, ' ').replace(/\r?\n/g, ' ').trim();

const ProjectManagementMob = () => {
  const { i18n, t } = useTranslation('translation');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const projects = useAppSelector(selectProjects);
  const status = useAppSelector(selectProjectsStatus);
  const session = useAppSelector(selectSession);
  const userProfile = useAppSelector(selectUserProfile);
  const [manageSheetProjectId, setManageSheetProjectId] = React.useState<string | null>(null);
  const [manageSelected, setManageSelected] = React.useState<'posts' | 'donors' | 'payments' | 'projectSettings'>(
    'posts',
  );
  const footerRef = React.useRef<HTMLElement | null>(null);
  const [showCreateFab, setShowCreateFab] = React.useState(true);
  const [confirmPayProject, setConfirmPayProject] = React.useState<{
    cardId: string;
    withdrawProjectId: string;
  } | null>(null);
  const [successPayProjectId, setSuccessPayProjectId] = React.useState<string | null>(null);
  const [failedPayProjectId, setFailedPayProjectId] = React.useState<string | null>(null);
  const [isProfileIncompleteModalOpen, setIsProfileIncompleteModalOpen] = React.useState(false);
  const [verifyingPayCodeById, setVerifyingPayCodeById] = React.useState<Record<string, boolean>>({});
  const [payFlowById, setPayFlowById] = React.useState<Record<string, PayFlowState>>({});
  const timersRef = React.useRef<Record<string, number>>({});

  const userId = useMemo(() => {
    return (
      userProfile?.user_id ||
      (session as any)?.user?._id ||
      (session as any)?.user?.id ||
      window.localStorage.getItem('missionfund.userId') ||
      ''
    );
  }, [session, userProfile?.user_id]);
  const isLoading =
    Boolean(userId) &&
    projects.length === 0 &&
    (status === 'idle' || status === 'loading');

  const userDeviceId = useMemo(() => {
    return (
      userProfile?.device_id ||
      (session as any)?.user?.device_id ||
      window.localStorage.getItem('missionfund.deviceId') ||
      ''
    );
  }, [session, userProfile?.device_id]);

  const payConfirmDescription = t('page.projectManagement.pay.confirmDescription');
  const paySuccessDescription = t('page.projectManagement.pay.successDescription');
  const payFailTitle = t('page.projectManagement.pay.failedTitle');
  const payFailDescription = t('page.projectManagement.pay.failedDescription');
  const currentLang = (i18n.resolvedLanguage || i18n.language || 'ko').split('-')[0];
  const isEnglish = currentLang === 'en';
  const renderCreateProjectLabel = () => {
    const label = t('page.projectManagement.createPrimary');
    if (label === 'Create New Fundraising Project') {
      return (
        <>
          Create New
          <br />
          Fundraising Project
        </>
      );
    }
    return label;
  };
  const renderMobilePayInfo = (step: PayFlowState['step']) => {
    if (step === 'processing') {
      return isEnglish ? (
        <>
          We'll send a verification code to your
          <br />
          bank account within 2 business days.
        </>
      ) : (
        <>
          영업일 기준 2일 이내 인증코드를 모금자님
          <br />
          계좌로 보내드립니다.
        </>
      );
    }

    return isEnglish ? (
      <>
        The funds will be deposited into
        <br />
        your account within 10 days.
      </>
    ) : (
      <>
        후원금을 10일 이내 모금자님 계좌로
        <br />
        입금해 드립니다.
      </>
    );
  };
  const renderMobileFailHint = (count: number) => {
    if (isEnglish) {
      return (
        <>
          Payout requests will be suspended after
          <br />
          {`3 failed attempts. (${count}/3)`}
        </>
      );
    }

    return t('page.projectManagement.pay.failHint', { count });
  };

  const toFundingTypeLabel = useCallback((raw?: string): FundingType => {
    const normalized = String(raw ?? '').toUpperCase();
    if (['OT', 'ONCE', 'ONE_TIME', 'ONETIME'].includes(normalized)) return 'oneTime';
    return 'recurring';
  }, []);

  useEffect(() => {
    if (!userId) return;

    dispatch(
      searchWithdrawProjects({
        limit: 100,
        params: [{ fld: 'user_id', val: userId, op: 'eq' }],
        sort: 'created_at:desc',
      }),
    );
  }, [dispatch, userId]);

  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach((t) => window.clearTimeout(t));
      timersRef.current = {};
    };
  }, []);

  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;
    const scrollContainer = el.closest('[data-mobile-scroll-main]');

    let frameId = 0;
    const updateCreateFabVisibility = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
        const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
        const isFooterVisible = visibleHeight > 0;
        setShowCreateFab(!isFooterVisible);
      });
    };

    const observer = new IntersectionObserver(
      updateCreateFabVisibility,
      { threshold: [0, 0.01, 1] },
    );

    observer.observe(el);
    updateCreateFabVisibility();
    scrollContainer?.addEventListener('scroll', updateCreateFabVisibility, { passive: true });
    window.addEventListener('scroll', updateCreateFabVisibility, { passive: true, capture: true });
    window.addEventListener('resize', updateCreateFabVisibility);
    window.visualViewport?.addEventListener('scroll', updateCreateFabVisibility);
    window.visualViewport?.addEventListener('resize', updateCreateFabVisibility);

    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frameId);
      scrollContainer?.removeEventListener('scroll', updateCreateFabVisibility);
      window.removeEventListener('scroll', updateCreateFabVisibility, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', updateCreateFabVisibility);
      window.visualViewport?.removeEventListener('scroll', updateCreateFabVisibility);
      window.visualViewport?.removeEventListener('resize', updateCreateFabVisibility);
    };
  }, []);

  const handleCreateProject = useCallback(() => {
    if (!canCreateNewProject(userProfile)) {
      setIsProfileIncompleteModalOpen(true);
      return;
    }
    navigate(PATHS.NEW_PROJECT);
  }, [navigate, userProfile]);

  const handleManageProject = useCallback((projectId: string) => {
    setManageSheetProjectId(projectId);
    setManageSelected('posts');
  }, []);

  const handleManageMove = useCallback(() => {
    if (!manageSheetProjectId) return;
    const state = { projectId: manageSheetProjectId };

    if (manageSelected === 'posts') navigate(PATHS.POST_MANAGEMENT, { state });
    if (manageSelected === 'donors') navigate(PATHS.DONOR_MANAGEMENT, { state });
    if (manageSelected === 'payments') navigate(PATHS.PAYMENT_STATUS, { state });
    if (manageSelected === 'projectSettings') navigate(PATHS.PROJECT_SETTINGS, { state });

    setManageSheetProjectId(null);
  }, [manageSelected, manageSheetProjectId, navigate]);

  const handlePayRequest = useCallback((cardId: string, withdrawProjectId: string) => {
    setConfirmPayProject({ cardId, withdrawProjectId });
  }, []);

  const syncProjectStatus = useCallback(
    async (cardId: string, withdrawProjectId: string, nextStatus: 'VC' | 'PD') => {
    if (!userId || !cardId) return;

      try {
        const targetProject = (projects as any[]).find(
          (project) => String(project?.project_id ?? '') === cardId || String(project?._id ?? '') === withdrawProjectId,
        );
        const updatePayload = targetProject?.project_id
          ? { ...targetProject, project_id: String(targetProject.project_id), status: nextStatus }
          : { project_id: cardId, status: nextStatus };
        await dispatch(updateProject(updatePayload as any)).unwrap();
      } catch {
        // ignore status sync failure; keep pay flow moving
      }

      try {
        await dispatch(updateUser({ user_id: userId } as any)).unwrap();
      } catch {
        // ignore user sync failure; keep pay flow moving
      }
    },
    [dispatch, projects, userId],
  );

  const startPayFlow = useCallback(async (cardId: string, withdrawProjectId: string) => {
    if (!userId || !cardId) return;
    setPayFlowById((prev) => ({ ...prev, [cardId]: { step: 'processing' } }));
    window.clearTimeout(timersRef.current[cardId]);

    const openExistingWithdrawCodeInput = async (fallbackWithdrawId?: string) => {
      try {
        const projectSnapshot = (projects as any[]).find(
          (project) => String(project?.project_id ?? '') === cardId || String(project?._id ?? '') === withdrawProjectId,
        );
        const projectData: Record<string, unknown> = projectSnapshot && typeof projectSnapshot === 'object'
          ? (projectSnapshot as Record<string, unknown>)
          : {};
        const withdrawId = String(
          fallbackWithdrawId
          || (projectData as any)?.withdraw_id
          || '',
        ).trim();
        if (!withdrawId) {
          const fallbackFlow = resolveWithdrawPayFlow({
            snapshot: projectData,
            withdrawId: '',
            code: String((projectData as any)?.request_authentication_code ?? (projectData as any)?.authentication_code ?? ''),
            requestCode: String((projectData as any)?.request_authentication_code ?? (projectData as any)?.authentication_code ?? ''),
            fallbackFailCount: toTrialCount((projectData as any)?.user_trials ?? (projectData as any)?.trials, 0),
          });
          setPayFlowById((prev) => ({
            ...prev,
            [cardId]: fallbackFlow,
          }));
          return;
        }

        let withdrawSnapshot: Record<string, unknown> = { ...projectData };
            const detail = await dispatch(getWithdraw({ withdraw_id: withdrawId } as any)).unwrap();
            if (detail && typeof detail === 'object') {
          withdrawSnapshot = {
            ...projectData,
            ...(detail as Record<string, unknown>),
          };
            }

        const existingCode = String(
          (withdrawSnapshot as any)?.authentication_code
          ?? (withdrawSnapshot as any)?.request_authentication_code
          ?? (withdrawSnapshot as any)?.response_authentication_code
          ?? '',
        );
        const requestCode = String(
          (withdrawSnapshot as any)?.request_authentication_code
          ?? (withdrawSnapshot as any)?.authentication_code
          ?? '',
        );
        const fallbackFailCount = toTrialCount(
          (withdrawSnapshot as any)?.user_trials ?? (withdrawSnapshot as any)?.trials,
          0,
        );
        const resolvedPayFlow = resolveWithdrawPayFlow({
          snapshot: withdrawSnapshot,
          withdrawId,
            code: existingCode || requestCode,
            requestCode,
          fallbackFailCount,
        });
        setPayFlowById((prev) => ({
          ...prev,
          [cardId]: resolvedPayFlow,
        }));
        if (resolvedPayFlow.step === 'done') {
          await syncProjectStatus(cardId, withdrawProjectId, 'PD');
        }
      } catch {
        const projectSnapshot = (projects as any[]).find(
          (project) => String(project?.project_id ?? '') === cardId || String(project?._id ?? '') === withdrawProjectId,
        );
        const projectData: Record<string, unknown> = projectSnapshot && typeof projectSnapshot === 'object'
          ? (projectSnapshot as Record<string, unknown>)
          : {};
        const fallbackFlow = resolveWithdrawPayFlow({
          snapshot: projectData,
          withdrawId: String((projectData as any)?.withdraw_id ?? ''),
          code: String((projectData as any)?.request_authentication_code ?? (projectData as any)?.authentication_code ?? ''),
          requestCode: String((projectData as any)?.request_authentication_code ?? (projectData as any)?.authentication_code ?? ''),
          fallbackFailCount: toTrialCount((projectData as any)?.user_trials ?? (projectData as any)?.trials, 0),
        });
        setPayFlowById((prev) => ({
          ...prev,
          [cardId]: fallbackFlow,
        }));
      }
    };
    try {
      const response = await dispatch(
        createWithdraw({
          project_id: cardId,
          user_id: userId,
          device_id: userDeviceId,
          mobile_os_type: 'IOS',
        }),
      ).unwrap();
      const resultCode = Number((response as any)?.result_Code ?? (response as any)?.result_code ?? -1);
      const resultMessageText = String((response as any)?.result_message ?? '');
      const createdWithdrawId = String(
        (response as any)?.withdraw_id
        ?? (response as any)?.result_message?.withdraw_id
        ?? (response as any)?.result_message?.insertedId
        ?? '',
      ).trim();
      const isDuplicateRequest = resultCode === 60001 || /duplicate key/i.test(resultMessageText);
      if (resultCode === 0 || isDuplicateRequest) {
        await syncProjectStatus(cardId, withdrawProjectId, 'VC');
        await openExistingWithdrawCodeInput(createdWithdrawId);
        return;
      }

      setPayFlowById((prev) => ({ ...prev, [cardId]: { step: 'idle' } }));
    } catch (error) {
      const errorCode = Number((error as any)?.result_Code ?? (error as any)?.result_code ?? -1);
      const errorText = String((error as any)?.result_message ?? (error as any)?.message ?? '');
      const isDuplicateRequest = errorCode === 60001 || /duplicate key/i.test(errorText);
      if (isDuplicateRequest) {
        await syncProjectStatus(cardId, withdrawProjectId, 'VC');
        await openExistingWithdrawCodeInput();
        return;
      }
      console.error(error);
      setPayFlowById((prev) => ({ ...prev, [cardId]: { step: 'idle' } }));
    }
  }, [dispatch, projects, syncProjectStatus, userDeviceId, userId]);

  const updatePayInput = useCallback((projectId: string, next: string) => {
    const digitsOnly = next.replace(/\D/g, '');
    setPayFlowById((prev) => {
      const cur = prev[projectId];
      if (!cur || cur.step !== 'codeReady') return prev;
      return { ...prev, [projectId]: { ...cur, input: digitsOnly, error: undefined } };
    });
  }, []);

  const clearPayInput = useCallback((projectId: string) => {
    setPayFlowById((prev) => {
      const cur = prev[projectId];
      if (!cur || cur.step !== 'codeReady') return prev;
      return { ...prev, [projectId]: { ...cur, input: '', error: undefined } };
    });
  }, []);

  const verifyPayCode = useCallback(
    async (projectId: string) => {
      const cur = payFlowById[projectId];
      if (!cur || cur.step !== 'codeReady') return;
      if (verifyingPayCodeById[projectId]) return;
      if (!cur.input.trim()) {
        setPayFlowById((prev) => ({
          ...prev,
          [projectId]: { ...cur, error: t('page.projectManagement.pay.invalidCode') },
        }));
      } else {
        setVerifyingPayCodeById((prev) => ({ ...prev, [projectId]: true }));
        try {
          const responseAuthenticationCodeAt = new Date().toISOString().replace(/\.\d{3}Z$/, '.000Z');
          const snapshot = (cur.withdrawSnapshot ?? {}) as Record<string, unknown>;
          const resolvedWithdrawId = String(
            cur.withdrawId
            || (snapshot as any)?.withdraw_id
            || '',
          ).trim();

          if (!resolvedWithdrawId) {
            setPayFlowById((prev) => ({
              ...prev,
              [projectId]: { ...cur, error: t('page.projectManagement.pay.invalidCode') },
            }));
            return;
          }

          // 서버 요구사항: /withdraw/get 응답값을 기준으로 put payload 구성
          let detailSnapshot: Record<string, unknown>;
          try {
            const detail = await dispatch(getWithdraw({ withdraw_id: resolvedWithdrawId } as any)).unwrap();
            detailSnapshot = (detail && typeof detail === 'object')
              ? (detail as Record<string, unknown>)
              : {};
          } catch {
            setPayFlowById((prev) => ({
              ...prev,
              [projectId]: { ...cur, error: t('page.projectManagement.pay.invalidCode') },
            }));
            return;
          }

          const putPayload: Record<string, unknown> = {
            ...detailSnapshot,
              request_authentication_code: cur.input.trim(),
              response_authentication_code_at: responseAuthenticationCodeAt,
          };
          if (!(putPayload as any).withdraw_id) {
            (putPayload as any).withdraw_id = resolvedWithdrawId;
          }

          const response = await dispatch(
            updateWithdraw(putPayload as any),
          ).unwrap();
          const resultCode = Number((response as any)?.result_Code ?? (response as any)?.result_code ?? -1);
          const statusCode = Number((response as any)?.statusCode ?? -1);
          if (resultCode === 0 || resultCode === 202 || statusCode === 200 || statusCode === 202) {
            let latestSnapshot: Record<string, unknown> = detailSnapshot;
            try {
              const latestDetail = await dispatch(getWithdraw({ withdraw_id: resolvedWithdrawId } as any)).unwrap();
              if (latestDetail && typeof latestDetail === 'object') {
                latestSnapshot = latestDetail as Record<string, unknown>;
              }
            } catch {
              // fallback to detailSnapshot from before /put
            }

            const latestCode = String(
              (latestSnapshot as any)?.authentication_code
              ?? (latestSnapshot as any)?.request_authentication_code
              ?? cur.input.trim(),
            );
            const latestRequestCode = String(
              (latestSnapshot as any)?.request_authentication_code
              ?? (latestSnapshot as any)?.authentication_code
              ?? cur.input.trim(),
            );
          const resolvedPayFlow = resolveWithdrawPayFlow({
            snapshot: latestSnapshot,
            withdrawId: resolvedWithdrawId,
            code: latestCode,
            requestCode: latestRequestCode,
            fallbackFailCount: cur.failCount,
          });

            setPayFlowById((prev) => ({
              ...prev,
            [projectId]: resolvedPayFlow,
            }));
          if (resolvedPayFlow.step === 'done') {
            await syncProjectStatus(projectId, projectId, 'PD');
          }
            setSuccessPayProjectId(projectId);
            return;
          }
        } catch (error) {
          console.error(error);
        } finally {
          setVerifyingPayCodeById((prev) => ({ ...prev, [projectId]: false }));
        }
      }

      const nextFail = cur.failCount + 1;
      if (nextFail >= 3) {
        setPayFlowById((prev) => ({ ...prev, [projectId]: { step: 'stopped', failCount: nextFail } }));
        setFailedPayProjectId(projectId);
        return;
      }

      setPayFlowById((prev) => ({
        ...prev,
        [projectId]: { ...cur, failCount: nextFail, error: t('page.projectManagement.pay.invalidCode') },
      }));
    },
    [dispatch, payFlowById, syncProjectStatus, t, verifyingPayCodeById],
  );

  const handleOpenProjectLink = useCallback((projectId: string) => {
    const url = `http://go.MissionFund.org/${projectId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const handleRequestConsulting = useCallback(() => {
    window.open('https://missionfund.org/support/', '_blank', 'noopener,noreferrer');
  }, []);

  const isDoneStatus = useCallback((statusValue?: string) => {
    const v = String(statusValue ?? '').toUpperCase();
    // 기본 종료 상태 판단(일시모금 VC/VS는 cards 계산에서 별도 처리)
    return v === 'DONE' || v === 'DN' || v === 'CM' || v === 'CL' || v === 'VC' || v === 'PD';
  }, []);

  useEffect(() => {
    setPayFlowById((prev) => {
      let changed = false;
      const next = { ...prev };

      projects.forEach((project) => {
        const cardId = String(project.project_id ?? project._id ?? project.title ?? '');
        if (!cardId) return;
        if (toFundingTypeLabel(project.type) !== 'oneTime') return;

        const existing = prev[cardId];
        if (existing && existing.step !== 'idle') return;

        const snapshot = (project && typeof project === 'object')
          ? (project as Record<string, unknown>)
          : {};
        const hasWithdrawSignals =
          'is_sent_authentication_code' in snapshot ||
          'is_response_authentication_code' in snapshot ||
          'is_sent_fund' in snapshot ||
          'user_trials' in snapshot ||
          'trials' in snapshot ||
          'withdraw_id' in snapshot;
        if (!hasWithdrawSignals) return;

        next[cardId] = resolveWithdrawPayFlow({
          snapshot,
          withdrawId: String((project as any)?.withdraw_id ?? ''),
          code: String((project as any)?.request_authentication_code ?? (project as any)?.authentication_code ?? ''),
          requestCode: String((project as any)?.request_authentication_code ?? (project as any)?.authentication_code ?? ''),
          fallbackFailCount: toTrialCount((project as any)?.user_trials ?? (project as any)?.trials, 0),
        });
        changed = true;
      });

      return changed ? next : prev;
    });
  }, [projects, toFundingTypeLabel]);

  const cards: ProjectCardItem[] = useMemo(() => {
    return projects.map((p) => {
      const fundingTypeLabel = toFundingTypeLabel(p.type);
      const projectStatus = String(p.status ?? '').toUpperCase();
      const isOneTimeRequestedStatus =
        fundingTypeLabel === 'oneTime' && (projectStatus === 'VC' || projectStatus === 'VS');
      const statusLabel: ProjectStatus = isOneTimeRequestedStatus
        ? 'inProgress'
        : isDoneStatus(projectStatus)
          ? 'done'
          : 'inProgress';
      const payAction: PayAction | undefined = fundingTypeLabel === 'oneTime' ? 'request' : undefined;
      return {
        id: String(p.project_id ?? p._id ?? p.title ?? ''),
        withdrawProjectId: String(p.project_id ?? p._id ?? ''),
        projectStatus,
        title: sanitizeProjectTitle(String(p.title ?? t('common.placeholders.untitledProject'))),
        dateText: String(p.start_date ?? p.created_at?.slice?.(0, 10) ?? ''),
        fundingTypeLabel,
        statusLabel,
        imageUrl: getCloudFrontImageUrl(getYouTubeThumbnail(p.video_url) || p.image_url, THUMBNAIL_DIMENSIONS),
        payAction,
      };
    });
  }, [isDoneStatus, projects, t, toFundingTypeLabel]);

  const toImageSrc = useCallback((imageUrl?: string) => {
    const trimmed = String(imageUrl ?? '').trim();
    if (!trimmed) return undefined;
    if (trimmed.startsWith('data:') || trimmed.startsWith('http') || trimmed.startsWith('/')) {
      return trimmed;
    }
    const mime = trimmed.startsWith('iVBORw0K')
      ? 'image/png'
      : trimmed.startsWith('R0lGOD')
        ? 'image/gif'
        : trimmed.startsWith('UklGR')
          ? 'image/webp'
          : 'image/jpeg';
    return `data:${mime};base64,${trimmed}`;
  }, []);

  if (isLoading) {
    return (
      <MobilePageLayout title={t('common.layout.projectManagement')} showBottomNav={false}>
        <DSLoading />
      </MobilePageLayout>
    );
  }

  if (cards.length > 0) {
    return (
      <MobilePageLayout title={t('common.layout.projectManagement')} footerRef={footerRef} showBottomNav={false}>
        <SectionTitle>{t('page.projectManagement.sectionTitle')}</SectionTitle>

        <Cards>
          {cards.map((card, idx) => {
            const payFlow = payFlowById[card.id] ?? ({ step: 'idle' } as PayFlowState);
            const isActiveCollecting = card.projectStatus === 'AC';
            const isProjectPaidDone = card.projectStatus === 'PD';
            const isProjectRequested = card.projectStatus === 'VC' || card.projectStatus === 'VS';
            const resolvedPayStep: PayFlowState['step'] =
              payFlow.step !== 'idle'
                ? payFlow.step
                : isActiveCollecting
                  ? 'idle'
                : isProjectPaidDone
                  ? 'done'
                  : isProjectRequested
                    ? 'processing'
                    : 'idle';
            const codeReadyFlow = payFlow.step === 'codeReady' ? payFlow : null;
            const stoppedFailCount = payFlow.step === 'stopped' ? payFlow.failCount : 3;
            const isPayDone = resolvedPayStep === 'done';
            const isSettlementProcessing =
              resolvedPayStep === 'processing' || resolvedPayStep === 'inProgress';
            const isSettling = resolvedPayStep === 'inProgress';
            const displayStatusLabel: ProjectStatus = isPayDone ? 'done' : card.statusLabel;
            const isDone = displayStatusLabel === 'done';
            const isPayFailed = resolvedPayStep === 'stopped';
            const isCodeInputDisabled = resolvedPayStep === 'stopped';
            const isVerifyingPayCode = Boolean(verifyingPayCodeById[card.id]);
            const imageSrc = toImageSrc(card.imageUrl);

            return (
              <React.Fragment key={card.id}>
                <Card $dimmed={isDone} $processing={isSettling}>
                  <Thumb $dimmed={isDone}>
                    {imageSrc ? (
                      <ThumbImg src={imageSrc} alt="" />
                    ) : (
                      <ThumbPlaceholder>
                        <ThumbLogo src={logoImg} alt="Mission Fund" />
                      </ThumbPlaceholder>
                    )}
                    <ChipRow>
                      <StatusChip $variant={displayStatusLabel}>
                        {displayStatusLabel === 'done' ? t('page.projectManagement.status.closed') : t(`page.projectManagement.status.${displayStatusLabel}`)}
                      </StatusChip>
                      <TypeChip
                        $noBorder={card.fundingTypeLabel === 'recurring'}
                        $dimmed={isDone}
                        $isOneTime={card.fundingTypeLabel === 'oneTime'}
                      >
                        {t(`page.projectManagement.fundingType.${card.fundingTypeLabel}`)}
                      </TypeChip>
                    </ChipRow>
                  </Thumb>

                  <CardBody $processing={isSettling}>
                    <CardTitle title={card.title} onClick={() => handleOpenProjectLink(card.id)} role="button" tabIndex={0}>
                      {card.title}
                    </CardTitle>
                    <CardDate>{card.dateText}</CardDate>

                    <ActionRow>
                      <ManageButton type="button" onClick={() => handleManageProject(card.id)}>
                        {t('page.projectManagement.manage.button')} <ManageArrowIcon src={weuiArrowIcon} alt="" />
                      </ManageButton>
                      <LinkButton type="button" onClick={() => handleOpenProjectLink(card.id)}>
                        <LinkIconImg src={linkIcon} alt={t('page.projectManagement.linkAlt')} />
                      </LinkButton>
                    </ActionRow>

                    {card.payAction ? (
                      <PayButton
                        type="button"
                        onClick={() => handlePayRequest(card.id, card.withdrawProjectId)}
                        disabled={
                          isPayDone ||
                          isPayFailed ||
                          isSettlementProcessing ||
                          resolvedPayStep === 'codeReady'
                        }
                        $done={isPayDone}
                        $failed={isPayFailed}
                        $processing={isSettlementProcessing}
                      >
                        {isPayDone
                          ? t('page.projectManagement.pay.status.completed')
                          : isPayFailed
                            ? t('page.projectManagement.pay.status.codeReady')
                          : isSettlementProcessing
                            ? (resolvedPayStep === 'inProgress' ? t('page.projectManagement.pay.settlementInProgress') : t('page.projectManagement.pay.status.processing'))
                            : resolvedPayStep === 'codeReady'
                              ? t('page.projectManagement.pay.status.codeReady')
                              : t('page.projectManagement.pay.status.request')}
                      </PayButton>
                    ) : null}

                    {card.payAction ? (
                      <>
                        {(resolvedPayStep === 'processing' || resolvedPayStep === 'inProgress') ? (
                          <PayInfoText>
                            {renderMobilePayInfo(resolvedPayStep)}
                          </PayInfoText>
                        ) : null}

                        {(resolvedPayStep === 'codeReady' || resolvedPayStep === 'stopped') ? (
                          <VerifyWrap>
                            <VerifyInputWrap $error={Boolean(codeReadyFlow?.error) || resolvedPayStep === 'stopped'} $disabled={isCodeInputDisabled}>
                              <VerifyLabelRow>
                                <VerifyLabel>{t('page.projectManagement.pay.verifyLabel')}</VerifyLabel>
                                {codeReadyFlow?.input ? (
                                  <ClearInputButton
                                    type="button"
                                    aria-label="clear"
                                    onClick={() => clearPayInput(card.id)}
                                  >
                                    <CloseCircleFilled />
                                  </ClearInputButton>
                                ) : null}
                              </VerifyLabelRow>
                              <VerifyInput
                                value={codeReadyFlow?.input ?? ''}
                                onChange={(e) => {
                                  if (isCodeInputDisabled || isVerifyingPayCode) return;
                                  updatePayInput(card.id, e.target.value);
                                }}
                                placeholder={t('page.projectManagement.pay.verifyPlaceholder')}
                                disabled={isCodeInputDisabled || isVerifyingPayCode}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                type="tel"
                              />
                            </VerifyInputWrap>
                            {resolvedPayStep === 'stopped'
                              ? <VerifyError>{t('page.projectManagement.pay.failStopped', { count: stoppedFailCount })}</VerifyError>
                              : codeReadyFlow?.error
                                ? <VerifyError>{codeReadyFlow.error}</VerifyError>
                                : null}
                            {resolvedPayStep === 'codeReady' && codeReadyFlow ? (
                            <VerifyHint>
                                {renderMobileFailHint(codeReadyFlow.failCount)}
                            </VerifyHint>
                            ) : null}
                            <VerifyButton
                              type="button"
                              onClick={() => verifyPayCode(card.id)}
                              disabled={isCodeInputDisabled || isVerifyingPayCode}
                              $loading={isVerifyingPayCode}
                              aria-busy={isVerifyingPayCode}
                            >
                              {isVerifyingPayCode ? (
                                <VerifyLoadingBox>
                                  <VerifyLoadingIcon src={loadingLottie} autoplay loop aria-hidden />
                                </VerifyLoadingBox>
                              ) : t('page.projectManagement.pay.verifyButton')}
                            </VerifyButton>
                          </VerifyWrap>
                        ) : null}
                      </>
                    ) : null}
                  </CardBody>
                </Card>
              </React.Fragment>
            );
          })}
        </Cards>

        {showCreateFab ? (
          <CreateFab type="button" onClick={handleCreateProject}>
            <CreateFabIcon
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 14 14"
              aria-hidden
              focusable="false"
            >
              <path d="M14 8H8V14H6V8H0V6H6V0H8V6H14V8Z" fill="white" />
            </CreateFabIcon>
            <span>{renderCreateProjectLabel()}</span>
          </CreateFab>
        ) : null}

        <DSModal
          open={Boolean(confirmPayProject)}
          title={t('page.projectManagement.pay.confirmTitle')}
          description={payConfirmDescription}
          descriptionVariant="body16"
          onClose={() => setConfirmPayProject(null)}
          actions={[
            { label: t('page.projectManagement.pay.confirmNo'), variant: 'secondary', onClick: () => setConfirmPayProject(null) },
            {
              label: t('page.projectManagement.pay.confirmYes'),
              variant: 'primary',
              onClick: () => {
                if (!confirmPayProject) return;
                const { cardId, withdrawProjectId } = confirmPayProject;
                setConfirmPayProject(null);
                startPayFlow(cardId, withdrawProjectId);
              },
            },
          ]}
        />

        <DSModal
          open={Boolean(successPayProjectId)}
          title={t('page.projectManagement.pay.successTitle')}
          description={paySuccessDescription}
          descriptionVariant="body16"
          onClose={() => setSuccessPayProjectId(null)}
          actions={[{ label: t('common.actions.confirm'), variant: 'primary', onClick: () => setSuccessPayProjectId(null) }]}
        />

        <DSModal
          open={Boolean(failedPayProjectId)}
          title={payFailTitle}
          description={payFailDescription}
          descriptionVariant="body16"
          onClose={() => setFailedPayProjectId(null)}
          actions={[{ label: t('common.actions.confirm'), variant: 'primary', onClick: () => setFailedPayProjectId(null) }]}
        />
        <ProfileIncompleteModal
          isOpen={isProfileIncompleteModalOpen}
          onClose={() => setIsProfileIncompleteModalOpen(false)}
          dismissible={false}
        />

        <DSBottomSheet open={Boolean(manageSheetProjectId)} onClose={() => setManageSheetProjectId(null)}>
          <SheetHeaderRow>
            <SheetTitle>{t('page.projectManagement.manage.sheetTitle')}</SheetTitle>
            <SheetClose type="button" onClick={() => setManageSheetProjectId(null)} aria-label="close">
              <CloseOutlined />
            </SheetClose>
          </SheetHeaderRow>

          <SheetOptionList>
            <SheetOptionButton type="button" onClick={() => setManageSelected('posts')}>
              <SheetRadio $checked={manageSelected === 'posts'} />
              {t('page.projectManagement.manage.posts')}
            </SheetOptionButton>
            <SheetOptionButton type="button" onClick={() => setManageSelected('donors')}>
              <SheetRadio $checked={manageSelected === 'donors'} />
              {t('page.projectManagement.manage.donors')}
            </SheetOptionButton>
            <SheetOptionButton type="button" onClick={() => setManageSelected('payments')}>
              <SheetRadio $checked={manageSelected === 'payments'} />
              {t('page.projectManagement.manage.payments')}
            </SheetOptionButton>
            <SheetOptionButton type="button" onClick={() => setManageSelected('projectSettings')}>
              <SheetRadio $checked={manageSelected === 'projectSettings'} />
              {t('page.projectManagement.manage.settings')}
            </SheetOptionButton>
          </SheetOptionList>

          <SheetAction type="button" onClick={handleManageMove}>
            {manageSelected === 'posts'
              ? t('page.projectManagement.manage.movePosts')
              : manageSelected === 'donors'
                ? t('page.projectManagement.manage.moveDonors')
                : manageSelected === 'payments'
                  ? t('page.projectManagement.manage.movePayments')
                  : t('page.projectManagement.manage.moveSettings')}
          </SheetAction>
        </DSBottomSheet>
      </MobilePageLayout>
    );
  }

  return (
    <MobilePageLayout title={t('common.layout.projectManagement')} showBottomNav={false}>
      <SectionTitle>{t('page.projectManagement.sectionTitle')}</SectionTitle>

      <CreateCard type="button" onClick={handleCreateProject}>
        <CreateIcon
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 110 110"
          aria-hidden
          focusable="false"
        >
          <rect width="110" height="110" rx="55" fill="#2BC2E1" />
          <path d="M28 56.875V52.75H52.75V28H56.875V52.75H81.625V56.875H56.875V81.625H52.75V56.875H28Z" fill="white" />
        </CreateIcon>
        <CreateText>{renderCreateProjectLabel()}</CreateText>
      </CreateCard>
      <ProfileIncompleteModal
        isOpen={isProfileIncompleteModalOpen}
        onClose={() => setIsProfileIncompleteModalOpen(false)}
        dismissible={false}
      />

      <ConsultingBox>
        <ConsultingHeader>
          <ConsultingIcon src={hugeIcon} alt="" aria-hidden />
          <ConsultingMessage>{t('page.projectManagement.consulting.message')}</ConsultingMessage>
        </ConsultingHeader>
        <ConsultingButton type="button" onClick={handleRequestConsulting}>
          <ButtonIcon
            xmlns="http://www.w3.org/2000/svg"
            width="30"
            height="30"
            viewBox="0 0 30 30"
            fill="none"
            aria-hidden
            focusable="false"
          >
            <path d="M26.25 15L17.5 6.25V11.25C8.75 12.5 5 18.75 3.75 25C6.875 20.625 11.25 18.625 17.5 18.625V23.75L26.25 15Z" fill="white" />
          </ButtonIcon>
          {t('page.projectManagement.consulting.action')}
        </ConsultingButton>
      </ConsultingBox>
      <EmptyDashboardSpacer aria-hidden />
    </MobilePageLayout>
  );
};

const SectionTitle = styled.h2`
  color: #17171b;
 ${theme.typography.head.semiBold24}
 margin-left: ${convertToVwMob(6)};
`;

const CreateFab = styled.button`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: calc(30px + env(safe-area-inset-bottom));
  z-index: 50;

  width: calc(100% - ${convertToVwMob(32)});
  max-width: ${convertToVwMob(520)};
  height: ${convertToVwMob(52)};

  display: inline-flex;
  width: ${convertToVwMob(298)};
  height: ${convertToVwMob(58)};
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  padding: ${convertToVwMob(14)} ${convertToVwMob(22)};
  border-radius: 50px;
  border: none;
  background: #026478;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  color: ${({ theme }) => theme.colors.grayScale[0]};
  ${theme.typography.button.semiBold20}
  cursor: pointer;

  span {
    line-height: 120%;
  }

  &:hover {
    border-radius: 50px;
    background: #026478;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  }

  &:focus,
  &:active {
    border-radius: 50px;
    background: var(--PrimaryBlue-80, #026478);
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  }
`;

const CreateFabIcon = styled.svg`
  width: ${convertToVwMob(14)};
  height: ${convertToVwMob(14)};
  flex: 0 0 auto;
`;

const CreateCard = styled.button`
  width: 100%;
  padding: ${convertToVwMob(26)} ${convertToVwMob(16)};
  border-radius: ${convertToVwMob(20)};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[20]};
  background: ${({ theme }) => theme.colors.other.mainBackground};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(12)};
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    border-radius: ${convertToVwMob(20)};
    background: #F8FDFF;
  }

  &:focus,
  &:focus-visible,
  &:active {
    border-radius: ${convertToVwMob(20)};
    background: #EBFCFF;
    outline: none;
  }
`;

const CreateIcon = styled.svg`
  width: ${convertToVwMob(76)};
  height: ${convertToVwMob(76)};
  flex: 0 0 auto;
  transition: filter 0.2s ease;

  ${CreateCard}:hover & {
    filter: drop-shadow(0 0 ${convertToVwMob(9.4)} #2BC2E1);
  }

  ${CreateCard}:focus &,
  ${CreateCard}:focus-visible &,
  ${CreateCard}:active & {
    filter: drop-shadow(0 0 ${convertToVwMob(9.4)} #2BC2E1)
      drop-shadow(0 0 ${convertToVwMob(20.45)} #2BC2E1);
  }
`;

const CreateText = styled.div`
  color: #2bc2e1;
  text-align: center;
  font-size: ${convertToVwMob(24)};
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
`;

const ConsultingBox = styled.section`
  padding: ${convertToVwMob(16)};
  border-radius: ${convertToVwMob(20)};
  border: 1px solid #fff3d0;
  background: #fffaea;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(12)};
`;

const EmptyDashboardSpacer = styled.div`
  height: ${convertToVwMob(220)};
  flex: 0 0 auto;
`;

const ConsultingHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${convertToVwMob(10)};
`;

const ConsultingIcon = styled.img`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  flex: 0 0 auto;
`;

const ConsultingMessage = styled.div`
  color: #17171b;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  white-space: pre-line;
`;

const ConsultingButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  padding: ${convertToVwMob(12)} ${convertToVwMob(16)};
  border-radius: ${convertToVwMob(999)};
  border: none;
  background: ${({ theme }) => theme.colors.yellow[30]};
  color: #ffffff;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
  cursor: pointer;

  &:hover {
    border-radius: 50px;
    background: #FFBE00;
    box-shadow: 0 0 5.4px 0 #FFBE00;
  }

  &:focus,
  &:focus-visible {
    border-radius: 50px;
    background: #FFAE00;
  }
`;

const ButtonIcon = styled.svg`
  width: ${convertToVwMob(30)};
  height: ${convertToVwMob(30)};
  flex: 0 0 auto;
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 23px
`;

const Card = styled.section<{ $dimmed: boolean; $processing?: boolean }>`
  border-radius: ${({ $dimmed }) => ($dimmed ? convertToVwMob(12) : convertToVwMob(20))};
  border: 1px solid #eee;
  background: ${({ $dimmed, $processing }) => ($dimmed || $processing ? '#F0F1F2' : '#ffffff')};
  overflow: hidden;
`;

const Thumb = styled.div<{ $dimmed?: boolean }>`
  position: relative;
  width: 100%;
  height: ${convertToVwMob(220)};
  background: #f3f3f3;
  border-top-left-radius: ${({ $dimmed }) =>
    $dimmed ? `calc(${convertToVwMob(12)} - 1px)` : `calc(${convertToVwMob(20)} - 1px)`};
  border-top-right-radius: ${({ $dimmed }) =>
    $dimmed ? `calc(${convertToVwMob(12)} - 1px)` : `calc(${convertToVwMob(20)} - 1px)`};
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.25);
    opacity: ${({ $dimmed }) => ($dimmed ? 1 : 0)};
    pointer-events: none;
    z-index: 1;
  }
`;

const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
  display: block;
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
`;

const ThumbPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f3f3;
`;

const ThumbLogo = styled.img`
  width: ${convertToVwMob(110)};
  height: auto;
  opacity: 0.85;
`;

const ChipRow = styled.div`
  position: absolute;
  bottom: ${convertToVwMob(10)};
  left: ${convertToVwMob(10)};
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(8)};
  z-index: 2;
`;

const BaseChip = styled.div`
  display: flex;
  padding: ${convertToVwMob(4)} ${convertToVwMob(12)};
  justify-content: center;
  align-items: center;
  gap: ${convertToVwMob(10)};
  border-radius: 4px;
  font-family: Pretendard;
  font-size: ${convertToVwMob(14)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  white-space: nowrap;
`;

const StatusChip = styled(BaseChip)<{ $variant: 'inProgress' | 'done' }>`
  background: ${({ $variant }) => ($variant === 'inProgress' ? '#2bc2e1' : '#929292')};
  color: ${({ $variant }) => ($variant === 'inProgress' ? '#ffffff' : '#E5E5E5')};
  border-radius: 4px;
`;

const TypeChip = styled(BaseChip)<{ $noBorder?: boolean; $dimmed?: boolean; $isOneTime?: boolean }>`
  background: #ffffff;
  border: ${({ $noBorder, $dimmed, $isOneTime }) => ($dimmed || $noBorder || $isOneTime ? 'none' : '1px solid #2bc2e1')};
  color: ${({ $dimmed, $isOneTime }) => ($dimmed || $isOneTime ? 'var(--grey-scale-90-main-text, #17171B)' : '#2bc2e1')};
`;

const CardBody = styled.div<{ $processing?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(6)};
  padding: ${convertToVwMob(20)};
  background: ${({ $processing }) => ($processing ? '#F0F1F2' : 'transparent')};
  height: auto;
  min-height: fit-content;
  overflow: visible;
`;

const CardTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  white-space: normal;
  word-break: keep-all;
  overflow-wrap: anywhere;
  cursor: pointer;
`;

const CardDate = styled.div`
  color: #676767;
  font-family: Pretendard;
  font-size: ${convertToVwMob(18)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(10)};
  margin-top: ${convertToVwMob(6)};
`;

const ManageArrowIcon = styled.img`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  flex-shrink: 0;
  display: block;
  object-fit: contain;
`;

const ManageButton = styled.button`
  display: inline-flex;
  height: ${convertToVwMob(54)};
  width: ${convertToVwMob(243)};
  flex: 1 1 auto;
  justify-content: center;
  align-items: center;
  gap: ${convertToVwMob(12)};
  border-radius: ${convertToVwMob(12)};
  border: ${convertToVwMob(1.6)} solid #e5e5e5;
  background: #ffffff;
  color: ${({ theme }) => theme.colors.grayScale[90]};
  ${theme.typography.contents.medium20}
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  line-height: 150%;

  &:hover:not(:disabled) {
    border-color: #2bc2e1;
    background: #f8fdff;
    color: ${({ theme }) => theme.colors.grayScale[90]};
  }

  &:active:not(:disabled) {
    border-color: #2bc2e1;
    background: #ebfcff;
    color: ${({ theme }) => theme.colors.grayScale[90]};
  }

  &:focus:not(:disabled) {
    outline: none;
    border-color: #2bc2e1;
    background: #ebfcff;
    color: ${({ theme }) => theme.colors.grayScale[90]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const PayButton = styled.button<{ $done: boolean; $failed?: boolean; $processing?: boolean }>`
  display: inline-flex;
  width: ${convertToVwMob(255)};
  height: ${convertToVwMob(54)};
  align-self: flex-start;
  justify-content: center;
  align-items: center;
  border-radius: ${convertToVwMob(12)};
  border: ${convertToVwMob(1.6)} solid #e5e5e5;
  background: ${({ $done }) => ($done ? '#bdbdbd' : '#ffffff')};
  color: ${({ $done, $failed, $processing }) =>
    $done ? '#e5e5e5' : $failed ? 'var(--grey-scale-50-disabled-text, #BCBCBC)' : $processing ? 'var(--Primary-Skyblue500, #357B89)' : '#17171b'};
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 600;
  line-height: 150%;
  cursor: pointer;
  margin-top: ${convertToVwMob(2)};

  &:disabled {
    cursor: not-allowed;
    opacity: ${({ $failed, $processing }) => ($failed || $processing ? 1 : 0.7)};
  }
`;

const PayInfoText = styled.div`
  margin-top: ${convertToVwMob(8)};
  color: ${({ theme }) => theme.colors.primaryBlue[70]};
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 400;
  line-height: 150%;
  white-space: pre-line;
`;

const VerifyWrap = styled.div`
  margin-top: ${convertToVwMob(10)};
  margin-left: ${convertToVwMob(-20)};
  margin-right: ${convertToVwMob(-20)};
  margin-bottom: ${convertToVwMob(-20)};
  border-radius: 0 0 ${convertToVwMob(20)} ${convertToVwMob(20)};
  border-top: 1.5px solid #EEE;
  border-bottom: none;
  border-left: none;
  background: #F8FDFF;
  padding: ${convertToVwMob(12)};
`;

const VerifyInputWrap = styled.div<{ $error: boolean; $disabled?: boolean }>`
  border-radius: ${convertToVwMob(12)};
  border: 1px solid ${({ $error, $disabled }) => ($disabled ? '#e3e5e8' : $error ? '#ff0f0f' : '#26c6da')};
  background: ${({ $disabled }) => ($disabled ? '#f5f6f7' : '#ffffff')};
  padding: ${convertToVwMob(10)} ${convertToVwMob(12)};
`;

const VerifyLabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${convertToVwMob(10)};
`;

const VerifyLabel = styled.div`
  color: #676767;
  font-family: Pretendard;
  font-size: ${convertToVwMob(12)};
  font-weight: 500;
  line-height: 120%;
`;

const ClearInputButton = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: #b5b7bb;
  font-size: ${convertToVwMob(18)};
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const VerifyInput = styled.input`
  margin-top: ${convertToVwMob(6)};
  width: 100%;
  border: 0;
  outline: none;
  font-family: Pretendard;
  font-size: ${convertToVwMob(18)};
  font-weight: 700;
  line-height: 150%;
  color: #17171b;

  &::placeholder {
    color: #b5b7bb;
    font-weight: 500;
  }

  &:disabled {
    background: transparent;
    color: #b5b7bb;
    cursor: not-allowed;
  }
`;

const VerifyError = styled.div`
  margin-top: ${convertToVwMob(8)};
  color: #ff0f0f;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 500;
  line-height: 150%;
`;

const VerifyHint = styled.div`
  margin-top: ${convertToVwMob(4)};
  color: #F00;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const VerifyButton = styled.button<{ $loading?: boolean }>`
  margin-top: ${convertToVwMob(10)};
  width: 100%;
  height: ${convertToVwMob(42)};
  border-radius: ${convertToVwMob(999)};
  border: 0;
  background: #357b89;
  color: #ffffff;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &:disabled {
    background: ${({ $loading }) => ($loading ? '#ebfcff' : '#f0f1f3')};
    color: ${({ $loading }) => ($loading ? '#2bc2e1' : '#b5b7bb')};
    cursor: not-allowed;
  }
`;

const VerifyLoadingBox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${convertToVwMob(60)};
  height: ${convertToVwMob(60)};
  overflow: hidden;
  flex: 0 0 ${convertToVwMob(60)};
`;

const VerifyLoadingIcon = styled(DotLottieReact)`
  display: block;
  width: 100% !important;
  height: 100% !important;
  transform: scale(1.35);
  transform-origin: center center;

  canvas,
  svg {
    width: 100% !important;
    height: 100% !important;
  }
`;

const LinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${convertToVwMob(54)};
  height: ${convertToVwMob(54)};
  border-radius: ${convertToVwMob(12)};
  border: none;
  background: #dff7fd;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const LinkIconImg = styled.img`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  object-fit: contain;
`;

const SheetHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
`;

const SheetTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: 28px;
  font-weight: 600;
  line-height: 1.2;
`;

const SheetClose = styled.button`
  border: none;
  background: transparent;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bcbcbc;
  
  svg {
    width: 32px;
    height: 32px;
  }
`;

const SheetOptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 48px;
`;

const SheetOptionButton = styled.button`
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 0;
  cursor: pointer;
  color: #17171b;
  font-family: Pretendard;
  font-size: 22px;
  font-weight: 400;
  line-height: 1.4;
  text-align: left;
`;

const SheetRadio = styled.div<{ $checked: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid ${({ $checked }) => ($checked ? '#2bc2e1' : '#eee')};
  background: #fff;
  position: relative;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #2bc2e1;
    display: ${({ $checked }) => ($checked ? 'block' : 'none')};
  }
`;

const SheetAction = styled.button`
  width: 100%;
  height: 68px;
  border-radius: 16px;
  border: none;
  background: #0b6773;
  color: #ffffff;
  font-family: Pretendard;
  font-size: 22px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default ProjectManagementMob;


